import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, NavDropdown, Form, FormControl, Button } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { useSite } from '../context/SiteContext';
import { getMediaUrl } from '../services/api';
import api from '../services/api';
import './Header.css';

/** True only for trade-license routes — not arbitrary slugs containing the word. */
const isTradeLicenseUrl = (url) => {
  if (!url) return false;
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
    return /^\/trade-license(\/|$)/.test(path);
  } catch {
    return /^\/trade-license(\/|$)/.test(url);
  }
};

/** Strip trade-license links for tenants without the module; keep all other items. */
const filterTradeLicenseMenus = (menus) => {
  if (!menus || !Array.isArray(menus)) return [];

  const filterItems = (items) => {
    if (!items || !Array.isArray(items)) return [];

    return items
      .map((item) => {
        const children = item.children && Array.isArray(item.children)
          ? filterItems(item.children)
          : [];

        if (children.length > 0) {
          // Keep dropdown parents even when their own URL points at trade-license
          return {
            ...item,
            url: isTradeLicenseUrl(item.url) ? '#' : item.url,
            children,
          };
        }

        // Leaf node — drop only direct trade-license links
        if (isTradeLicenseUrl(item.url)) return null;
        return item;
      })
      .filter(Boolean);
  };

  return menus.map((menu) => ({
    ...menu,
    items: filterItems(menu.items),
  }));
};

const Header = () => {
  const { config, moduleAccess } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [rawMenus, setRawMenus] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState(false);

  // Fetch once; derive visible items from module access (avoids race / double-fetch)
  useEffect(() => {
    const loadMenus = async () => {
      try {
        const response = await api.get('/menus/', { params: { location: 'header' } });
        const data = Array.isArray(response.data)
          ? response.data
          : (response.data?.results || []);
        setRawMenus(data);
      } catch (error) {
        console.error('Error loading menus:', error);
      }
    };
    loadMenus();
  }, []);

  const menus = useMemo(
    () => (moduleAccess.trade_license ? rawMenus : filterTradeLicenseMenus(rawMenus)),
    [rawMenus, moduleAccess.trade_license],
  );

  // Smooth scroll handler for logo hide/show on mobile
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.site-header');
      if (window.innerWidth <= 992) {
        if (window.scrollY > 30) {
          header?.classList.add('scrolled');
        } else {
          header?.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Optional: Scroll handling effect
  useEffect(() => {
    // Keep just the scroll detection here
  }, [config]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setExpanded(false);
    }
  };

  const renderMenuItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <NavDropdown
          key={item.id}
          title={
            <>
              {item.label} <i className="fas fa-chevron-down ms-1" style={{ fontSize: '0.8em' }}></i>
            </>
          }
          id={`nav-dropdown-${item.id}`}
          className="nav-dropdown-menu"
          align="start"
        >
          {item.children.map(child => renderSubmenuItem(child))}
        </NavDropdown>
      );
    }

    return (
      <Nav.Link
        key={item.id}
        as={Link}
        to={item.url || '#'}
        onClick={() => setExpanded(false)}
      >
        {item.label}
      </Nav.Link>
    );
  };

  const renderSubmenuItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <div key={item.id} className="dropdown-submenu">
          <NavDropdown.Item
            as="div"
            className="dropdown-toggle dropdown-submenu-toggle"
          >
            {item.label}
            <span className="submenu-arrow">›</span>
            <div className="dropdown-menu dropdown-submenu-menu">
              {item.children.map(child => renderSubmenuItem(child))}
            </div>
          </NavDropdown.Item>
        </div>
      );
    }

    return (
      <NavDropdown.Item
        key={item.id}
        as={Link}
        to={item.url || '#'}
        onClick={() => setExpanded(false)}
        target={item.open_new_tab ? "_blank" : undefined}
      >
        {item.label}
      </NavDropdown.Item>
    );
  };

  return (
    <header className="site-header">
      {/* Main Header */}
      <div className="header-main">
        <Container>
          <div className="header-content">
            <div className="site-identity">
              {config?.logo && (
                <Link to="/" className="logo-link">
                  <img
                    src={getMediaUrl(config.logo)}
                    alt={config.site_name}
                    className="site-logo"
                  />
                </Link>
              )}
              <div className="site-info">
                <h1 className="site-name">{config?.site_name}</h1>
                <p className="site-tagline">{config?.site_tagline}</p>
              </div>
            </div>

            {/* Swachh Bharat Logo */}
            <div className="header-right">
              <a
                href="https://swachhbharatmission.gov.in"
                target="_blank"
                rel="noopener noreferrer"
                className="swachh-bharat-logo"
                title="Swachh Bharat Mission"
              >
                <img
                  src="/swach-bharat.png"
                  alt="Swachh Bharat"
                  className="swachh-logo-img"
                />
                {/* <span className="swachh-text">Swachh<br />Bharat</span> */}
              </a>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Navigation */}
      <Navbar
        variant="dark"
        expand="lg"
        className={`main-navigation nav-anim-${config?.header_animation || 'none'}`}
        style={{ background: config?.header_bg_color || undefined }}
        expanded={expanded}
        sticky="top"
      >
        <Container>
          <Navbar.Toggle
            aria-controls="main-navbar"
            onClick={() => setExpanded(!expanded)}
            className="navbar-toggle"
          />
          <Navbar.Collapse id="main-navbar">
            <Nav as="ul" className="navbar-nav main-nav">
              {menus.map((menu) =>
                (menu.items && Array.isArray(menu.items)
                  ? menu.items.map((item) => renderMenuItem(item))
                  : [])
              )}
            </Nav>

            {/* Search in Navigation */}
            {config?.show_search_bar !== false && (
              <Form className="d-flex ms-auto" onSubmit={handleSearch}>
                <FormControl
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search"
                  className="me-2"
                  style={{ minWidth: '250px' }}
                />
                <Button variant="outline-light" type="submit">
                  <FaSearch />
                </Button>
              </Form>
            )}
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
};

export default Header;
